// React
import React, { useEffect, useMemo, useState } from 'react';

// Mantine
import {
  Button,
  Flex,
  Group,
  Stack,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import {
  MRT_ColumnFiltersState,
  MRT_EditActionButtons,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_TableOptions,
} from 'mantine-react-table';

// Refine
import { useNotification, useTranslation } from '@refinedev/core';
import type { ListProps } from '@refinedev/mantine';
import { useDocumentTitle } from '@refinedev/react-router-v6';

import { useQuery } from '@tanstack/react-query';

// Custom Components
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';
import { TableAlertBanner } from 'components/common/TableAlertBanner';
import { TableProgressBar } from 'components/common/TableProgressBar';
import type {
  TariffUploadFiles,
  TariffUploadSubmission,
} from './components/TariffFileUploadModal';
import { TariffFileUploadModal } from './components/TariffFileUploadModal';

// Utils
import { uploadFileToS3 } from 'utils/fileUpload';
import { buttonTextColor } from 'utils/buttonUtils';

// Interface
import { Tariff } from 'interfaces';

// Columns
import { TariffColumns } from 'columns/Tariff';

// Resource
import {
  bulkIngestTariffs,
  getBulkTariffIngestJob,
  useCreateTariff,
  usePaginatedTariffs,
  useUpdateTariff,
} from 'api/resource';
import {
  CreateMutationDataParams,
  UpdateMutationDataParams,
  createMutationData,
  updateMutationData,
} from 'api/utils/MutationDataBuilder';

import {
  TariffIngestResult,
  BulkTariffIngestJob,
  isBCTariffIngestResult,
} from '@billing/validation';
import * as Sentry from '@sentry/react';

const TERMINAL_JOB_STATUSES = new Set([
  'COMPLETED',
  'COMPLETED_WITH_ERRORS',
  'FAILED',
]);

const useTariffIngestJob = ({
  jobID,
  enabled,
}: {
  jobID?: string;
  enabled: boolean;
}) =>
  useQuery({
    queryKey: ['tariff-bulk-ingest-job', jobID],
    queryFn: () => {
      if (!jobID) throw new Error('Tariff upload job ID is missing');
      return getBulkTariffIngestJob({ jobId: jobID });
    },
    enabled: Boolean(jobID && enabled),
    refetchInterval: (query) =>
      TERMINAL_JOB_STATUSES.has(query.state.data?.status ?? '') ? false : 2000,
    refetchIntervalInBackground: true,
    retry: false,
  });

export const TariffsManagement: React.FC<ListProps> = () => {
  const computedColorScheme = useComputedColorScheme('dark');
  const { translate } = useTranslation();
  const title = translate('tariffs.management.title', 'Tariff Management');
  const [tariffFiles, setTariffFiles] = useState<TariffUploadFiles>({});
  const [bulkUploadView, setBulkUploadView] = useState<boolean>(false);
  const [tariffUploadResult, setTariffUploadResult] =
    useState<TariffIngestResult | null>(null);
  const [tariffUploadJob, setTariffUploadJob] =
    useState<BulkTariffIngestJob | null>(null);
  const [isUploadingTariffs, setIsUploadingTariffs] = useState<boolean>(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const { open } = useNotification();
  useDocumentTitle(
    translate('tariffs.management.documentTitle', 'Tariffs | QBilling')
  );
  const tariffBucket = `tariff-files-${import.meta.env.VITE_ACCOUNT_ID}`;

  const {
    data: paginatedTariffs,
    isLoading,
    isError,
    isFetching,
    error: errorTariffData,
    refetch: refetchTariffs,
  } = usePaginatedTariffs({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    filters: columnFilters,
    globalFilter,
    sorting,
  });
  const tariffs = paginatedTariffs?.data ?? [];

  const createTariff = useCreateTariff();
  const updateTariff = useUpdateTariff();
  const tariffUploadJobID = tariffUploadJob?.jobId;

  const tariffUploadJobQuery = useTariffIngestJob({
    jobID: tariffUploadJobID,
    enabled: isUploadingTariffs && !tariffUploadResult,
  });

  useEffect(() => {
    const job = tariffUploadJobQuery.data;
    if (!job) return;

    setTariffUploadJob(job);

    if (job.status === 'FAILED') {
      open?.({
        key: 'error-uploading-tariffs',
        message: translate(
          'tariffs.management.processingError',
          'There was an error processing the tariff files'
        ),
        description: job.error,
        type: 'error',
      });
      setIsUploadingTariffs(false);
      return;
    }

    if (job.status !== 'COMPLETED' && job.status !== 'COMPLETED_WITH_ERRORS') {
      return;
    }

    if (!job.result) {
      open?.({
        key: 'error-uploading-tariffs',
        message: translate(
          'tariffs.management.missingResult',
          'Tariff upload completed without a result'
        ),
        type: 'error',
      });
      setIsUploadingTariffs(false);
      return;
    }

    const result = job.result;
    setTariffUploadResult(result);

    const getResultDetails = () => {
      if (isBCTariffIngestResult(result)) {
        return {
          shouldRefetch: result.mode === 'APPLY',
          hasIssues: result.failedCount > 0 || result.unsupportedCount > 0,
          description: [
            `${result.newCount} ${translate('tariffs.ingest.newLower', 'new')}`,
            `${result.changedCount} ${translate(
              'tariffs.ingest.changedLower',
              'changed'
            )}`,
            `${result.unchangedCount} ${translate(
              'tariffs.ingest.unchangedLower',
              'unchanged'
            )}`,
            `${result.unsupportedCount} ${translate(
              'tariffs.ingest.unsupportedLower',
              'unsupported'
            )}`,
            `${result.failedCount} ${translate(
              'tariffs.ingest.failedLower',
              'failed'
            )}`,
          ].join(', '),
        };
      }

      return {
        shouldRefetch: true,
        hasIssues: result.failedCount > 0,
        description: [
          `${result.createdCount} ${translate(
            'tariffs.ingest.createdLower',
            'created'
          )}`,
          `${result.skippedCount} ${translate(
            'tariffs.ingest.skippedLower',
            'skipped'
          )}`,
          `${result.failedCount} ${translate(
            'tariffs.ingest.failedLower',
            'failed'
          )}`,
        ].join(', '),
      };
    };

    const { description, hasIssues, shouldRefetch } = getResultDetails();
    if (shouldRefetch) refetchTariffs();
    open?.({
      key: 'tariff-uploaded',
      message: translate(
        'tariffs.management.uploadComplete',
        'Tariff upload complete'
      ),
      description,
      type: hasIssues ? 'error' : 'success',
    });
    setIsUploadingTariffs(false);
  }, [open, refetchTariffs, tariffUploadJobQuery.data, translate]);

  useEffect(() => {
    if (!tariffUploadJobQuery.error) return;

    open?.({
      key: 'error-uploading-tariffs',
      message: translate(
        'tariffs.management.statusError',
        'There was an error checking the tariff upload status'
      ),
      type: 'error',
    });
    Sentry.captureException(tariffUploadJobQuery.error);
    setIsUploadingTariffs(false);
  }, [open, tariffUploadJobQuery.error, translate]);

  const handleBulkUploadView = () => {
    setTariffFiles({});
    setTariffUploadResult(null);
    setTariffUploadJob(null);
    setBulkUploadView(true);
  };

  const handleCreateTariff = async ({
    table,
    values,
  }: CreateMutationDataParams<Tariff>) =>
    createMutationData<Tariff>({
      table,
      values,
      mutation: createTariff,
    });

  const handleEditTariff = async ({
    table,
    values,
    row,
  }: UpdateMutationDataParams<Tariff>) =>
    updateMutationData<Tariff>({
      table,
      values,
      row,
      mutation: updateTariff,
    });

  const handleSubmitFiles = async ({
    province,
    effectiveDate,
    mode,
  }: TariffUploadSubmission) => {
    let shouldKeepUploading = false;
    try {
      setIsUploadingTariffs(true);
      setTariffUploadResult(null);
      setTariffUploadJob(null);

      const createIngestJob = async (): Promise<BulkTariffIngestJob | null> => {
        if (province === 'BC') {
          const tariffFile = tariffFiles.tariff;
          if (!tariffFile || !effectiveDate) {
            open?.({
              key: 'error-uploading-tariffs',
              message: translate(
                'tariffs.management.bcFilesRequired',
                'Please upload a BC fee schedule file and choose an effective date'
              ),
              type: 'error',
            });
            return null;
          }
          await uploadFileToS3([tariffFile], tariffBucket);
          return bulkIngestTariffs({
            format: 'BC_MSP_FIXED_WIDTH',
            province: 'BC',
            tariffFile: tariffFile.name,
            effectiveDate,
            mode,
          });
        }

        const { rate, description } = tariffFiles;
        if (!rate || !description) {
          open?.({
            key: 'error-uploading-tariffs',
            message: translate(
              'tariffs.management.mbFilesRequired',
              'Please upload both rate and description files'
            ),
            type: 'error',
          });
          return null;
        }
        await uploadFileToS3([rate, description], tariffBucket);
        return bulkIngestTariffs({
          format: 'MB_FIXED_WIDTH',
          province: 'MB',
          rateFile: rate.name,
          descriptionFile: description.name,
          mode: 'APPLY',
        });
      };

      const job = await createIngestJob();
      if (!job) return;
      shouldKeepUploading = true;
      setTariffUploadJob(job);
    } catch (error) {
      open?.({
        key: 'error-uploading-tariffs',
        message: translate(
          'tariffs.management.uploadError',
          'There was an error uploading the tariff files'
        ),
        type: 'error',
      });
      Sentry.captureException(error);
    } finally {
      if (!shouldKeepUploading) {
        setIsUploadingTariffs(false);
      }
    }
  };

  const data = useMemo(() => tariffs, [tariffs]);
  const columns = TariffColumns(translate);
  const tableOptions: MRT_TableOptions<Tariff> = {
    data,
    columns,
    state: {
      pagination,
      columnFilters,
      globalFilter,
      sorting,
      isLoading,
      showProgressBars: isFetching,
      showAlertBanner: isError,
    },
    rowCount: paginatedTariffs?.total ?? 0,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    onColumnFiltersChange: (updater) => {
      setColumnFilters((previous) =>
        typeof updater === 'function' ? updater(previous) : updater
      );
      setPagination((previous) => ({ ...previous, pageIndex: 0 }));
    },
    onGlobalFilterChange: (updater) => {
      setGlobalFilter((previous) =>
        typeof updater === 'function' ? updater(previous) : updater
      );
      setPagination((previous) => ({ ...previous, pageIndex: 0 }));
    },
    onSortingChange: (updater) => {
      setSorting((previous) =>
        typeof updater === 'function' ? updater(previous) : updater
      );
      setPagination((previous) => ({ ...previous, pageIndex: 0 }));
    },
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    mantineEditRowModalProps: {
      closeOnClickOutside: true,
      size: 'lg',
      overlayProps: {
        opacity: 0.15,
        blur: 5,
      },
      style: {
        tableLayout: 'auto',
      },
    },
    enableFullScreenToggle: false,
    enableEditing: true,
    enableColumnResizing: true,
    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    mantineToolbarAlertBannerProps() {
      return TableAlertBanner({ isError, message: errorTariffData?.message });
    },
    onCreatingRowSave: handleCreateTariff,
    onEditingRowSave: handleEditTariff,
    renderTopToolbarCustomActions: ({ table }) => (
      <>
        <Flex justify="left" direction="column" gap="md">
          <Group>
            <Button
              c={buttonTextColor(computedColorScheme, false)}
              onClick={() => table.setCreatingRow(true)}
            >
              {translate('tariffs.management.add', 'Add New Tariff')}
            </Button>
            <Button
              c={buttonTextColor(computedColorScheme, false)}
              onClick={() => handleBulkUploadView()}
            >
              {translate('tariffs.management.bulkUpload', 'Bulk Upload')}
            </Button>
            <TariffFileUploadModal
              opened={bulkUploadView}
              onClose={() => setBulkUploadView(false)}
              tariffFiles={tariffFiles}
              setTariffFiles={(files) => {
                setTariffFiles(files);
                setTariffUploadResult(null);
                setTariffUploadJob(null);
              }}
              handleSubmitFiles={handleSubmitFiles}
              uploadResult={tariffUploadResult}
              uploadJob={tariffUploadJob}
              isUploading={isUploadingTariffs}
            />
          </Group>
        </Flex>
      </>
    ),
    renderCreateRowModalContent: ({ table, row, internalEditComponents }) => (
      <Stack>
        <Title order={3}>
          {translate('tariffs.management.create', 'Create New Tariff')}
        </Title>
        {internalEditComponents}
        <Flex justify="end">
          <MRT_EditActionButtons table={table} row={row} variant="text" />
        </Flex>
      </Stack>
    ),
    renderEditRowModalContent: ({ table, row, internalEditComponents }) => (
      <Stack>
        <Title order={3}>
          {translate('tariffs.management.edit', 'Edit Tariff')}
        </Title>
        {internalEditComponents}
        <Flex justify="space-around">
          <MRT_EditActionButtons table={table} row={row} variant="text" />
        </Flex>
      </Stack>
    ),
  };

  return (
    <BillingDataGridWrapper
      title={title}
      tableOptions={tableOptions}
      dataTestID="tariffManagement"
    />
  );
};
